import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, Search, User, Calendar, Flag, Bot, Clock, DollarSign } from 'lucide-react';
import { MarkdownContent } from '@/components/chat/MarkdownContent';
import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import { format } from 'date-fns';

const log = createComponentLogger('ConversationViewer', 'ai-conversations');

// Format agent type for display
const formatAgentType = (agentType: string): string => {
  const mapping: Record<string, string> = {
    encompass_helper: 'Encompass Helper',
    pre_approval: 'Pre-Approval',
    document_checklist: 'Document Checklist',
    mortgage_education: 'Mortgage Education',
    investor_guidelines: 'Investor Guidelines',
    wisr: 'Wisr Assistant',
    general: 'General'
  };
  return mapping[agentType] || agentType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

interface Conversation {
  id: string;
  user_id: string;
  organization_id: string;
  agent_type: string;
  message_count: number;
  total_tokens: number;
  total_cost: number;
  created_at: string;
  last_message_at: string;
  metadata?: Record<string, unknown>;
  user_email?: string;
}

interface Message {
  id: string;
  conversation_id: string;
  role: 'user' | 'assistant';
  content: string;
  model_used?: string;
  tokens_input?: number;
  tokens_output?: number;
  cost_usd?: number;
  response_time_ms?: number;
  documents_used?: string[];
  user_rating?: number;
  user_feedback?: string;
  flagged?: boolean;
  created_at: string;
}

export default function ConversationViewer() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAgent, setSelectedAgent] = useState<string>('all');
  const [dateRange, setDateRange] = useState<string>('30days');
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);

  // Load conversations on mount
  useEffect(() => {
    loadConversations();
    // loadConversations intentionally re-runs only when the user changes a server-side filter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, selectedAgent]);

  async function loadConversations() {
    try {
      setIsLoading(true);

      // Get user's organization
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
return;
}

      const { data: userData } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      if (!userData?.organization_id) {
return;
}

      // Build query
      let query = supabase
        .from('ai_internal_chat_conversations')
        .select(`
          *,
          users!ai_internal_chat_conversations_user_id_fkey(email)
        `)
        .eq('organization_id', userData.organization_id)
        .order('last_message_at', { ascending: false });

      // Apply date range filter
      if (dateRange !== 'all') {
        const now = new Date();
        const startDate = new Date();

        switch (dateRange) {
          case 'today':
            startDate.setHours(0, 0, 0, 0);
            break;
          case '7days':
            startDate.setDate(now.getDate() - 7);
            break;
          case '30days':
            startDate.setDate(now.getDate() - 30);
            break;
          case '90days':
            startDate.setDate(now.getDate() - 90);
            break;
        }

        query = query.gte('created_at', startDate.toISOString());
      }

      // Apply agent filter
      if (selectedAgent !== 'all') {
        query = query.eq('agent_type', selectedAgent);
      }

      const { data: conversationsData, error } = await query;

      if (error) {
throw error;
}

      // Map the data to include user email
      const mappedConversations = (conversationsData || []).map((conv: Conversation & { users?: { email?: string } | null }) => ({
        ...conv,
        user_email: conv.users?.email
      }));

      setConversations(mappedConversations);

      // Select first conversation by default
      if (mappedConversations.length > 0 && !selectedConversation) {
        handleConversationSelect(mappedConversations[0]);
      }

      log.info('Conversations loaded', { count: mappedConversations.length });
    } catch (error) {
      log.error('Failed to load conversations', {}, error as Error);
    } finally {
      setIsLoading(false);
    }
  }

  async function loadMessages(conversationId: string) {
    try {
      setIsLoadingMessages(true);

      const { data: messagesData, error } = await supabase
        .from('ai_internal_chat_messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (error) {
throw error;
}

      setMessages(messagesData || []);
      log.info('Messages loaded', { conversationId, count: messagesData?.length || 0 });
    } catch (error) {
      log.error('Failed to load messages', { conversationId }, error as Error);
    } finally {
      setIsLoadingMessages(false);
    }
  }

  function handleConversationSelect(conversation: Conversation) {
    setSelectedConversation(conversation);
    loadMessages(conversation.id);
  }

  // Filter conversations by search query
  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery) {
return true;
}
    const query = searchQuery.toLowerCase();
    return (
      conv.user_email?.toLowerCase().includes(query) ||
      conv.agent_type?.toLowerCase().includes(query) ||
      conv.id.toLowerCase().includes(query)
    );
  });

  // Get flagged messages count
  const flaggedCount = messages.filter(m => m.flagged).length;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Conversation List */}
      <Card className="lg:col-span-1">
        <CardHeader>
          <CardTitle>Conversations</CardTitle>
          <CardDescription>View and analyze AI chat history</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8"
            />
          </div>

          {/* Filters */}
          <div className="grid grid-cols-2 gap-2">
            <Select value={selectedAgent} onValueChange={setSelectedAgent}>
              <SelectTrigger>
                <SelectValue placeholder="All Agents" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Agents</SelectItem>
                <SelectItem value="encompass_helper">Encompass Helper</SelectItem>
                <SelectItem value="pre_approval">Pre-Approval</SelectItem>
                <SelectItem value="general">General</SelectItem>
              </SelectContent>
            </Select>

            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger>
                <SelectValue placeholder="Date Range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="7days">Last 7 Days</SelectItem>
                <SelectItem value="30days">Last 30 Days</SelectItem>
                <SelectItem value="90days">Last 90 Days</SelectItem>
                <SelectItem value="all">All Time</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Conversation List */}
          <ScrollArea className="h-[500px]">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No conversations found
              </div>
            ) : (
              <div className="space-y-2">
                {filteredConversations.map((conv) => (
                  <div
                    key={conv.id}
                    onClick={() => handleConversationSelect(conv)}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedConversation?.id === conv.id
                        ? 'bg-primary/10 border border-primary'
                        : 'hover:bg-muted'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <User className="h-3 w-3 text-muted-foreground" />
                        <span className="text-sm font-medium truncate">
                          {conv.user_email || 'Unknown User'}
                        </span>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {conv.message_count} msgs
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Bot className="h-3 w-3" />
                        <span>{formatAgentType(conv.agent_type)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        <span>{format(new Date(conv.last_message_at), 'MMM d')}</span>
                      </div>
                    </div>
                    {conv.total_cost > 0 && (
                      <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                        <DollarSign className="h-3 w-3" />
                        <span>${conv.total_cost.toFixed(4)}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Conversation Detail */}
      <Card className="lg:col-span-2">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Conversation Detail</CardTitle>
              {selectedConversation && (
                <CardDescription>
                  {selectedConversation.user_email} • {format(new Date(selectedConversation.created_at), 'PPpp')}
                </CardDescription>
              )}
            </div>
            {flaggedCount > 0 && (
              <Badge variant="destructive" className="flex items-center gap-1">
                <Flag className="h-3 w-3" />
                {flaggedCount} Flagged
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[550px]">
            {!selectedConversation ? (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                Select a conversation to view details
              </div>
            ) : isLoadingMessages ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : messages.length === 0 ? (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                No messages in this conversation
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`p-4 rounded-lg ${
                      message.role === 'user'
                        ? 'bg-muted ml-auto max-w-[80%]'
                        : 'bg-primary/5 mr-auto max-w-[80%]'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {message.role === 'user' ? (
                          <User className="h-4 w-4" />
                        ) : (
                          <Bot className="h-4 w-4" />
                        )}
                        <span className="font-medium text-sm">
                          {message.role === 'user' ? 'User' : 'Assistant'}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(message.created_at), 'HH:mm:ss')}
                      </span>
                    </div>

                    <MarkdownContent content={message.content} className="text-sm" />

                    {message.role === 'assistant' && (
                      <div className="mt-2 pt-2 border-t flex items-center gap-4 text-xs text-muted-foreground">
                        {message.model_used && (
                          <span>{message.model_used}</span>
                        )}
                        {message.response_time_ms && (
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            <span>{message.response_time_ms}ms</span>
                          </div>
                        )}
                        {message.cost_usd && (
                          <div className="flex items-center gap-1">
                            <DollarSign className="h-3 w-3" />
                            <span>${message.cost_usd.toFixed(4)}</span>
                          </div>
                        )}
                        {message.documents_used && message.documents_used.length > 0 && (
                          <Badge variant="outline" className="text-xs">
                            {message.documents_used.length} docs
                          </Badge>
                        )}
                        {message.flagged && (
                          <Badge variant="destructive" className="text-xs">
                            <Flag className="h-3 w-3 mr-1" />
                            Flagged
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
